import { useCallback, useEffect, useRef, useState } from "react";
import { roomApi, toMessage, toPerson } from "./roomApi";
import { useAdaptivePoll } from "./useAdaptivePoll";
import { useRoomStream } from "./useRoomStream";

// The roster changes when people arrive and leave, which is rare next to
// chat, so it keeps a plain interval. The heartbeat has to keep running even
// with the tab in the background: a student reading their notes in another
// window is still in the lesson, and stopping would mark them absent.
const ROSTER_POLL_MS = 15000;
const HEARTBEAT_MS = 30000;

/**
 * A live session room: its chat and who is in it.
 *
 * Polling rather than a socket, deliberately — it is the same REST the rest of
 * the app speaks, it survives a dropped connection without reconnect logic,
 * and `since` makes each poll cost only what has been said since the last one.
 * The shape here is what a socket would produce, so swapping the transport
 * later does not reach the components.
 *
 * Without a sessionGuid it runs entirely in memory, which is what the
 * standalone board and the previews want.
 */
export function useRoom({
  sessionGuid,
  currentUser,
  isInstructor,
  onBoardPushed,
  onBoardOps,
  onBoardLive,
  onSubRooms,
  onActivity,
  onSessionEnded,
}) {
  const [messages, setMessages] = useState([]);
  const [roster, setRoster] = useState([]);
  const [error, setError] = useState(null);
  const lastSeen = useRef(null);
  const live = Boolean(sessionGuid);

  const merge = useCallback(
    (rows) => {
      if (!rows.length) return;
      const incoming = rows.map((r) => toMessage(r, currentUser?.guid));
      lastSeen.current = rows[rows.length - 1].created_date;
      setMessages((prev) => {
        // Keyed by id, because a message we posted optimistically comes back
        // from the poll and must not appear twice.
        const byId = new Map(prev.map((m) => [m.id, m]));
        incoming.forEach((m) => byId.set(m.id, m));
        return [...byId.values()].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      });
    },
    [currentUser?.guid]
  );

  const loadRoster = useCallback(async () => {
    if (!live) return;
    try {
      const rows = await roomApi.getRoster(sessionGuid);
      setRoster(rows.map(toPerson));
    } catch {
      /* the roster keeps its last good value */
    }
  }, [live, sessionGuid]);

  // Chat. The cadence adapts: quick while the room is talking, backing off to
  // every 30s while it is not, and paused entirely when nobody is looking.
  const pollMessages = useCallback(async () => {
    if (!live) return false;
    try {
      const rows = await roomApi.getMessages(sessionGuid, lastSeen.current);
      merge(rows);
      setError(null);
      return rows.length > 0;
    } catch (e) {
      // Not worth interrupting a lesson for; `since` has not moved, so the
      // next poll catches up on everything missed.
      setError("Reconnecting…");
      return false;
    }
  }, [live, sessionGuid, merge]);

  // The stream is how the room actually keeps up: the server pushes what
  // happened, in milliseconds rather than seconds.
  const stream = useRoomStream({
    sessionGuid,
    enabled: live,
    handlers: {
      onMessage: (row) => merge([row]),
      onAnswered: (ids) => {
        const set = new Set(ids);
        const at = new Date().toISOString();
        setMessages((prev) => prev.map((m) => (set.has(m.id) ? { ...m, answeredAt: at } : m)));
      },
      onBoard: (doc, by) => onBoardPushed?.(doc, by),
      onBoardOps: (ops, by) => onBoardOps?.(ops, by),
      onBoardLive: (kind, payload, by) => onBoardLive?.(kind, payload, by),
      onSubRooms: (rooms) => onSubRooms?.(rooms),
      onActivity: (guid) => onActivity?.(guid),
      onSessionEnded: () => onSessionEnded?.(),
      onArrived: () => loadRoster(),
    },
  });

  // The poll stays underneath as a safety net, not as the mechanism. While the
  // stream is connected it runs rarely and finds nothing; if the stream is
  // unavailable it is the whole room again, exactly as before.
  const chatPoll = useAdaptivePoll(pollMessages, { enabled: live, floorOnly: stream.connected });

  // Who is here: polled, and fetched again the moment the stream says
  // someone came in.
  useEffect(() => {
    if (!live) return undefined;
    const id = setInterval(loadRoster, ROSTER_POLL_MS);
    loadRoster();
    return () => clearInterval(id);
  }, [live, loadRoster]);

  // Being here.
  useEffect(() => {
    if (!live) return undefined;
    const beat = () => roomApi.heartbeat(sessionGuid).catch(() => {});
    beat();
    const id = setInterval(beat, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [live, sessionGuid]);

  const send = useCallback(
    async (text) => {
      const trimmed = (text || "").trim();
      if (!trimmed) return;
      if (!live) {
        setMessages((prev) => [
          ...prev,
          {
            id: `local-${Date.now()}`,
            text: trimmed,
            authorGuid: currentUser?.guid,
            authorName: "You",
            authorInitial: (currentUser?.initial || "?").toUpperCase(),
            isOwn: true,
            isQuestion: false,
            createdAt: new Date().toISOString(),
          },
        ]);
        return;
      }
      try {
        const row = await roomApi.postMessage(sessionGuid, trimmed);
        merge([row]);
        // Someone has just spoken; the room is live again.
        chatPoll.quicken();
      } catch (e) {
        console.error(e);
        setError("That message didn't send.");
      }
    },
    [live, sessionGuid, merge, currentUser, chatPoll]
  );

  const markAnswered = useCallback(
    async (ids) => {
      const set = new Set(ids);
      const at = new Date().toISOString();
      // Cleared straight away: the teacher has just answered, and waiting for
      // a round trip would leave the group sitting in the monitor.
      setMessages((prev) => prev.map((m) => (set.has(m.id) ? { ...m, answeredAt: at } : m)));
      if (!live) return;
      try {
        await roomApi.markAnswered(sessionGuid, ids);
      } catch (e) {
        console.error(e);
      }
    },
    [live, sessionGuid]
  );

  /** Only unanswered student questions reach the monitor. */
  const questions = messages
    .filter((m) => m.isQuestion && !m.isOwn && !m.answeredAt)
    .map((m) => ({
      id: m.id,
      text: m.text,
      studentName: m.authorName,
      studentGuid: m.authorGuid,
      createdAt: m.createdAt,
    }));

  return {
    messages,
    questions,
    roster,
    error,
    isLive: live,
    send,
    markAnswered,
    // Exposed so the room can show how closely it is watching, and so a
    // deliberate action elsewhere can pull the next poll forward.
    pollIntervalMs: chatPoll.intervalMs,
    refresh: chatPoll.pollNow,
    // Whether the room is being pushed to or is falling back to asking.
    isStreaming: stream.connected,
  };
}
